import React from 'react';
import type {HTMLAttributes} from 'react';
import './DataTable.css';
import {openableProps} from './openable.js';
import InlineNotice from './InlineNotice.jsx';
// eslint-disable-next-line no-unused-vars -- JSX-only use
import EmptyState, {type EmptyStateAction} from './EmptyState.jsx';
// eslint-disable-next-line no-unused-vars -- JSX-only use
import SectionBand from './SectionBand.jsx';

type DataTableRow = Record<string, unknown>;
type DataTableRowProps = HTMLAttributes<HTMLTableRowElement> & {
  [dataAttribute: `data-${string}`]: string | number | undefined;
};

type DataTableColumn<Row extends DataTableRow> = {
  key: string;
  label: React.ReactNode;
  align?: 'left' | 'right';
  primary?: boolean;
  sortable?: boolean;
  mobilePriority?: boolean;
  render?: (row: Row) => React.ReactNode;
};

type DataTableSection<Row extends DataTableRow> = {
  key?: string;
  label: React.ReactNode;
  count?: React.ReactNode;
  rows?: Row[];
};

type DataTableSort = {key: string; dir: 'asc' | 'desc'};

// The shared phone card (Design Constitution L13, Q31, Table G): below 640px a
// record list shows each row as a card — a bold main value, up to two gray
// lines and at most one badge (the row's worst status). Lines are composed per
// list ('Farrowed Oct 6 · Farrowing Barn'), so the caller renders them.
export type DataTableCard<Row extends DataTableRow> = {
  main: (row: Row) => React.ReactNode;
  line1?: (row: Row) => React.ReactNode;
  line2?: (row: Row) => React.ReactNode;
  badge?: (row: Row) => React.ReactNode;
};

type DataTableProps<Row extends DataTableRow> = {
  columns?: DataTableColumn<Row>[];
  rows?: Row[];
  sections?: DataTableSection<Row>[];
  rowKey?: string | ((row: Row) => React.Key);
  density?: 'comfortable' | 'compact';
  onRowOpen?: (row: Row) => void;
  rowDisabled?: (row: Row) => boolean;
  rowProps?: (row: Row) => DataTableRowProps;
  rowStyle?: (row: Row) => React.CSSProperties | undefined;
  selectable?: boolean;
  selectedIds?: Set<React.Key> | React.Key[];
  onToggleSelect?: (row: Row) => void;
  showRowNumbers?: boolean;
  sort?: DataTableSort;
  onSort?: (key: string) => void;
  loading?: boolean;
  loadError?: {message?: string | null} | null;
  onRetry?: () => void;
  emptyMessage?: React.ReactNode;
  emptyAction?: EmptyStateAction | null;
  surfaceKey?: string;
  stickyTop?: number | string;
  maxInitialRows?: number;
  card?: DataTableCard<Row>;
};

// ============================================================================
// DataTable — the canonical table system · CP0 §A6 / §A7 / §A9
// ----------------------------------------------------------------------------
// Guard-aware by construction so it SATISFIES the locked contracts instead of
// fighting them:
//   • real <table>; sticky <thead>; numbers right-aligned + tabular-nums;
//     hairline rows; no zebra.
//   • each <tr> carries `.hoverable-row` and opens the record via onRowOpen
//     (no per-row Open button); keyboard-openable (Enter/Space).
//   • emits data-${surfaceKey}-loaded and gates rows + empty behind !loadError.
//   • loading -> skeleton rows; empty & loaded -> <EmptyState>; loadError ->
//     <InlineNotice> + Retry, stale rows cleared.
//   • optional leftmost select column; locked rows render disabled.
//   • optional Active/Complete section bands.
//   • phones (<640px): with `card`, each row is the shared phone card (bold
//     main value, two gray lines, one worst-of badge, chevron when it opens;
//     L13, Q31). Without `card`, rows still collapse to stacked label/value
//     lines until the list's area wave gives it its Table G card; columns
//     with mobilePriority:false are hidden there.
//
// Props:
//   columns: [{ key, label, align?:'left'|'right', primary?, sortable?,
//               mobilePriority?:boolean, render?:(row)=>node }]
//   rows: object[]                          (flat list)            OR
//   sections: [{ key, label, count?, rows }] (banded list; takes precedence)
//   rowKey: string | (row)=>string          (default 'id')
//   density: 'comfortable' | 'compact'
//   onRowOpen?: (row) => void
//   rowDisabled?: (row) => boolean
//   selectable?: boolean
//   selectedIds?: Set|array
//   onToggleSelect?: (row) => void
//   sort?: { key, dir:'asc'|'desc' }, onSort?: (key) => void
//   loading?, loadError?: {message}|null, onRetry?, emptyMessage?, emptyAction?
//   surfaceKey: string                       (for data-<key>-loaded)
//   stickyTop?: number|string                (sticky header offset)
//   card?: { main, line1?, line2?, badge? }  (row => node; the phone card)
// ============================================================================

function keyFor<Row extends DataTableRow>(row: Row, rowKey: DataTableProps<Row>['rowKey'], i: number): React.Key {
  if (typeof rowKey === 'function') return rowKey(row);
  return (row?.[rowKey || 'id'] as React.Key | null | undefined) ?? i;
}

function cellClass<Row extends DataTableRow>(col: DataTableColumn<Row>) {
  const c = [];
  if (col.align === 'right') c.push('is-num');
  if (col.primary) c.push('is-primary');
  if (col.mobilePriority === false) c.push('mobile-hide');
  return c.join(' ');
}

export default function DataTable<Row extends DataTableRow = DataTableRow>({
  columns = [],
  rows,
  sections,
  rowKey = 'id',
  density = 'comfortable',
  onRowOpen,
  rowDisabled,
  rowProps,
  rowStyle,
  selectable = false,
  selectedIds,
  onToggleSelect,
  showRowNumbers = false,
  sort,
  onSort,
  loading = false,
  loadError = null,
  onRetry,
  emptyMessage = 'Nothing here yet.',
  emptyAction,
  surfaceKey = 'data-table',
  stickyTop = 0,
  maxInitialRows,
  card,
}: DataTableProps<Row>) {
  const isSelected = (k: React.Key) => {
    if (!selectedIds) return false;
    if (selectedIds instanceof Set) return selectedIds.has(k);
    return Array.isArray(selectedIds) && selectedIds.includes(k);
  };

  // Leading columns (select / row-number) widen the colSpan for bands/skeletons.
  const leadCols = (selectable ? 1 : 0) + (showRowNumbers ? 1 : 0);
  // CP0 WI-6: openable rows reserve a trailing chevron column; the › is painted
  // by the global `.hoverable-row .chev` rule on hover/focus (no layout shift).
  const chevCol = onRowOpen ? 1 : 0;
  const totalCols = columns.length + leadCols + chevCol;

  const loaded = !loading && !loadError;
  const flatRows = sections ? sections.flatMap((s) => s.rows || []) : rows || [];
  const isEmpty = loaded && flatRows.length === 0;

  // §5.2 perf: cap rendered rows for very long flat lists (e.g. dailys with
  // thousands of records) so the DOM stays bounded. The full row set stays in
  // memory, so export/print/filters/saved-views are unaffected; "Show more"
  // reveals the next page. No cap when maxInitialRows is unset (other tables
  // render every row exactly as before). Reset on row-count change.
  const pageStep = maxInitialRows || 0;
  const [visibleCount, setVisibleCount] = React.useState(pageStep || Infinity);
  React.useEffect(() => {
    setVisibleCount(pageStep || Infinity);
  }, [pageStep, flatRows.length]);

  function openRow(row: Row, disabled: boolean) {
    if (disabled || !onRowOpen) return;
    onRowOpen(row);
  }

  function renderBodyRow(row: Row, i: number) {
    const k = keyFor(row, rowKey, i);
    const disabled = rowDisabled ? rowDisabled(row) : false;
    const openable = !!onRowOpen && !disabled;
    return (
      <tr
        key={k}
        className={`${openable ? 'hoverable-row' : ''}${disabled ? ' is-disabled' : ''}`}
        data-row-id={k}
        style={rowStyle ? rowStyle(row) : undefined}
        {...(rowProps ? rowProps(row) : {})}
        {...(openable ? openableProps(() => openRow(row, disabled)) : {})}
      >
        {selectable && (
          <td className="select-col" data-label="" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              aria-label="Select row"
              checked={isSelected(k)}
              disabled={disabled}
              onChange={() => onToggleSelect && onToggleSelect(row)}
            />
          </td>
        )}
        {showRowNumbers && (
          <td className="row-num" data-label="#">
            {i + 1}
          </td>
        )}
        {columns.map((col) => (
          <td key={col.key} className={cellClass(col)} data-label={col.label}>
            {col.render ? col.render(row) : (row?.[col.key] as React.ReactNode)}
          </td>
        ))}
        {openable && (
          <td className="chev-col mobile-hide" aria-hidden="true">
            <span className="chev">{'›'}</span>
          </td>
        )}
        {card && <PhoneCardCell row={row} card={card} openable={openable} />}
      </tr>
    );
  }

  return (
    <div className="data-table-wrap" data-surface={surfaceKey} {...{[`data-${surfaceKey}-loaded`]: loaded ? '1' : '0'}}>
      {loadError && (
        <div data-datatable-error="1" style={{padding: '4px 0'}}>
          <InlineNotice notice={{kind: 'error', message: loadError.message || 'Failed to load.'}} />
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              style={{
                padding: '8px 14px',
                borderRadius: 10,
                border: '1px solid #b91c1c',
                background: '#b91c1c',
                color: 'white',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'inherit',
              }}
            >
              Retry
            </button>
          )}
        </div>
      )}

      {!loadError && (
        <table
          className="data-table"
          data-density={density}
          data-phone={card ? 'card' : undefined}
          style={
            {
              ['--data-table-sticky-top']: typeof stickyTop === 'number' ? `${stickyTop}px` : stickyTop,
            } as React.CSSProperties
          }
        >
          <thead>
            <tr>
              {selectable && <th className="select-col" aria-label="Select" />}
              {showRowNumbers && <th className="row-num">#</th>}
              {columns.map((col) => {
                const sortable = !!col.sortable && !!onSort;
                const isActive = sort && sort.key === col.key;
                return (
                  <th
                    key={col.key}
                    className={`${col.align === 'right' ? 'is-num' : ''}${sortable ? ' is-sortable' : ''}`}
                    onClick={sortable ? () => onSort(col.key) : undefined}
                    aria-sort={isActive ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {col.label}
                    {isActive && <span className="sort-caret">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
                  </th>
                );
              })}
              {onRowOpen && <th className="chev-col" aria-hidden="true" />}
            </tr>
          </thead>
          <tbody>
            {loading &&
              Array.from({length: 5}).map((_, r) => (
                <tr key={`sk-${r}`}>
                  <td className="skeleton-cell" colSpan={totalCols}>
                    <div className="skeleton-bar" />
                  </td>
                </tr>
              ))}

            {!loading &&
              sections &&
              sections.map((s) => (
                <React.Fragment key={(s.key || s.label) as React.Key}>
                  <SectionBand as="tr" span={totalCols} label={s.label} count={s.count ?? (s.rows || []).length} />
                  {(s.rows || []).map((row, i) => renderBodyRow(row, i))}
                </React.Fragment>
              ))}

            {!loading && !sections && (rows || []).slice(0, visibleCount).map((row, i) => renderBodyRow(row, i))}
            {!loading && !sections && (rows || []).length > visibleCount && (
              <tr>
                <td colSpan={totalCols} style={{padding: '10px 14px', textAlign: 'center'}}>
                  <button
                    type="button"
                    data-datatable-show-more="1"
                    onClick={() => setVisibleCount((c) => c + (pageStep || 200))}
                    style={{
                      padding: '7px 16px',
                      borderRadius: 10,
                      border: '1px solid var(--border-strong)',
                      background: 'var(--surface)',
                      color: 'var(--text-primary)',
                      fontWeight: 600,
                      fontSize: 12,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    {`Show ${Math.min(pageStep || 200, (rows || []).length - visibleCount)} more (${
                      (rows || []).length - visibleCount
                    } remaining)`}
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {isEmpty && <EmptyState message={emptyMessage} action={emptyAction} />}
    </div>
  );
}

// The phone card's one cell. Hidden on wider screens (DataTable.css); on a
// phone it is the row's only visible cell, so it carries the content itself.
function PhoneCardCell<Row extends DataTableRow>({
  row,
  card,
  openable,
}: {
  row: Row;
  card: DataTableCard<Row>;
  openable: boolean;
}) {
  const badge = card.badge ? card.badge(row) : null;
  const line1 = card.line1 ? card.line1(row) : null;
  const line2 = card.line2 ? card.line2(row) : null;
  return (
    <td className="data-card-cell">
      <span className="data-card-main">{card.main(row)}</span>
      {badge != null && badge !== false && <span className="data-card-badge">{badge}</span>}
      {openable && (
        <span className="data-card-chev chev" aria-hidden="true">
          {'›'}
        </span>
      )}
      {line1 != null && line1 !== false && <span className="data-card-line">{line1}</span>}
      {line2 != null && line2 !== false && <span className="data-card-line">{line2}</span>}
    </td>
  );
}
